import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import './Contact.css';
import SEO, { seoConfig } from '../components/SEO';

export default function Contact({ t }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    consent: false,
    _honey: '' // honeypot anti-spam : doit rester vide
  });
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: false
  });

  // FormSubmit : backend de formulaire gratuit, sans compte ni clé.
  // Les messages arrivent sur cette adresse. Au TOUT PREMIER envoi, FormSubmit
  // envoie un email d'activation à confirmer une seule fois.
  const CONTACT_EMAIL = 'daniela.parito@gmail.com';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation côté client (backstop ; les champs portent aussi required/minLength)
    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();
    if (
      !formData.consent ||
      name.length < 2 || name.length > 100 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ||
      message.length < 10 || message.length > 5000
    ) {
      setStatus({ submitting: false, submitted: false, error: true });
      setTimeout(() => setStatus(prev => ({ ...prev, error: false })), 5000);
      return;
    }

    setStatus({ submitting: true, submitted: false, error: false });

    try {
      const response = await fetch(`https://formsubmit.co/ajax/${CONTACT_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name,
          email,
          message,
          _replyto: email,
          _subject: `Nouveau message de ${name} — Site DP Architecture`,
          _template: 'table',
          _captcha: 'false',
          _honey: formData._honey, // honeypot : rempli = bot = ignoré
        })
      });

      const result = await response.json();

      // FormSubmit renvoie success sous forme de chaîne "true"/"false"
      if (response.ok && String(result.success) === 'true') {
        setStatus({ submitting: false, submitted: true, error: false });
        setFormData({ name: '', email: '', message: '', consent: false, _honey: '' });

        // Reset success message after 5 seconds
        setTimeout(() => {
          setStatus(prev => ({ ...prev, submitted: false }));
        }, 5000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({ submitting: false, submitted: false, error: true });
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setStatus(prev => ({ ...prev, error: false }));
      }, 5000);
    }
  };

  return (
    <div className="contact-page">
      {/* SEO Component */}
      <SEO 
        title={seoConfig.contact.title}
        description={seoConfig.contact.description}
        keywords={seoConfig.contact.keywords}
      />
      
      <div className="page-container">
        <ScrollReveal>
          <h1 className="page-title">{t.contact.title}</h1>
        </ScrollReveal>
        <div className="contact-grid">
          {/* Contact Form */}
          <ScrollReveal delay={0.2}>
            <form className="contact-form" onSubmit={handleSubmit}>
              {/* Honeypot anti-spam FormSubmit (caché aux humains, piège à bots) */}
              <input
                type="text"
                name="_honey"
                value={formData._honey}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
              />
              
              <div className="form-group">
                <label htmlFor="contact-name" className="sr-only">{t.contact.name}</label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contact.name}
                  className="form-input"
                  required
                  minLength={2}
                  maxLength={100}
                  aria-required="true"
                  disabled={status.submitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email" className="sr-only">{t.contact.email}</label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contact.email}
                  className="form-input"
                  required
                  maxLength={150}
                  aria-required="true"
                  disabled={status.submitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-message" className="sr-only">{t.contact.message}</label>
                <textarea
                  name="message"
                  id="contact-message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.message}
                  rows={6}
                  className="form-textarea"
                  required
                  minLength={10}
                  maxLength={5000}
                  aria-required="true"
                  disabled={status.submitting}
                />
              </div>
              
              <label className="form-consent">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                  disabled={status.submitting}
                />
                <span>
                  {t.contact.consentText || "J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la"}{' '}
                  <Link to="/privacy-policy">{t.contact.consentLink || 'politique de confidentialité'}</Link>.
                </span>
              </label>

              <button
                type="submit"
                className="form-submit"
                disabled={status.submitting}
              >
                {status.submitting ? (
                  <>
                    <Loader2 size={18} className="spinner" />
                    {t.contact.sending || 'Envoi en cours...'}
                  </>
                ) : (
                  t.contact.send
                )}
              </button>

              {/* Statut d'envoi — annoncé aux lecteurs d'écran */}
              <div aria-live="polite" aria-atomic="true">
                {/* Success Message */}
                {status.submitted && (
                  <div className="form-message success">
                    <CheckCircle size={20} />
                    <span>{t.contact.success || 'Votre message a été envoyé avec succès !'}</span>
                  </div>
                )}

                {/* Error Message */}
                {status.error && (
                  <div className="form-message error">
                    <AlertCircle size={20} />
                    <span>{t.contact.error || 'Une erreur est survenue. Veuillez réessayer.'}</span>
                  </div>
                )}
              </div>
            </form>
          </ScrollReveal>
          
          {/* Contact Info */}
          <ScrollReveal delay={0.4}>
            <div className="contact-info">
              <div>
                <h3 className="info-title">{t.contact.info}</h3>
              </div>
              <div className="info-item">
                <MapPin className="info-icon" size={20} />
                <div>
                  <p className="info-text">{t.contact.address}</p>
                  <p className="info-text">{t.contact.city}</p>
                </div>
              </div>
              <div className="info-item">
                <Phone className="info-icon" size={20} />
                <a href="tel:+33614902513" className="info-link">
                  06 14 90 25 13
                </a>
              </div>
              <div className="info-item">
                <Mail className="info-icon" size={20} />
                <a href="mailto:daniela.parito@gmail.com" className="info-link">
                  daniela.parito@gmail.com
                </a>
              </div>
              <div className="info-image">
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800"
                  alt="Cabinet d'architecture Daniela Parito à Montpellier - 663 Rue du pré aux clercs"
                  className="office-image"
                  loading='lazy'
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}